import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, CheckSquare, Download } from "lucide-react";
import TaskForm from "@/components/tasks/TaskForm";
import TasksList from "@/components/tasks/TasksList";

export default function Tasks() {
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [parentTask, setParentTask] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [relatedFilter, setRelatedFilter] = useState("all");

  const queryClient = useQueryClient();

  const { data: allTasks = [], isLoading } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list("-created_date"),
  });

  const { data: timeEntries = [] } = useQuery({
    queryKey: ["all-time-entries"],
    queryFn: () => base44.entities.TimeEntry.list(),
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => base44.entities.Project.list(),
  });

  // Filter out subtasks from main list
  const tasks = allTasks.filter(task => !task.parent_task_id);

  const createTaskMutation = useMutation({
    mutationFn: (data) => base44.entities.Task.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setShowForm(false);
      setEditingTask(null);
      setParentTask(null);
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Task.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setShowForm(false);
      setEditingTask(null);
      setParentTask(null);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id) => base44.entities.Task.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const handleSubmit = (data) => {
    const taskData = parentTask ? { ...data, parent_task_id: parentTask.id } : data;
    if (editingTask) {
      updateTaskMutation.mutate({ id: editingTask.id, data: taskData });
    } else {
      createTaskMutation.mutate(taskData);
    }
  };

  const handleAddSubtask = (task) => {
    setParentTask(task);
    setEditingTask(null);
    setShowForm(true);
  };

  const handleStatusChange = (task, newStatus) => {
    updateTaskMutation.mutate({ 
      id: task.id, 
      data: { ...task, status: newStatus } 
    });
  };

  const exportToCSV = () => {
    const headers = ["Tittel", "Status", "Prioritet", "Tildelt til", "Forfallsdato", "Prosjekt", "Relatert til", "Beskrivelse"];
    const rows = filteredTasks.map(task => [
      task.title || "",
      task.status || "",
      task.priority || "",
      task.assigned_to_name || "",
      task.due_date || "",
      task.project_name || "",
      task.related_to_name || "",
      task.description || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `oppgaver_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = 
      task.title?.toLowerCase().includes(search.toLowerCase()) ||
      task.description?.toLowerCase().includes(search.toLowerCase()) ||
      task.assigned_to_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || task.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || task.priority === priorityFilter;
    const matchesRelated = relatedFilter === "all" || task.related_to_type === relatedFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesRelated;
  });

  // Group tasks by status
  const tasksByStatus = {
    todo: filteredTasks.filter(t => t.status === "todo"),
    in_progress: filteredTasks.filter(t => t.status === "in_progress"),
    completed: filteredTasks.filter(t => t.status === "completed"),
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Oppgaver</h1>
            <p className="text-slate-500 mt-1">{tasks.length} totalt oppgaver</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={exportToCSV} variant="outline" className="border-slate-200">
              <Download className="w-4 h-4 mr-2" /> Eksporter CSV
            </Button>
            <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Opprett Oppgave
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Søk oppgaver..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-white border-0 shadow-sm"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Statuser</SelectItem>
              <SelectItem value="todo">Å Gjøre</SelectItem>
              <SelectItem value="in_progress">Pågår</SelectItem>
              <SelectItem value="completed">Fullført</SelectItem>
              <SelectItem value="cancelled">Kansellert</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Prioritet</SelectItem>
              <SelectItem value="low">Lav</SelectItem>
              <SelectItem value="medium">Middels</SelectItem>
              <SelectItem value="high">Høy</SelectItem>
              <SelectItem value="urgent">Haster</SelectItem>
            </SelectContent>
          </Select>
          <Select value={relatedFilter} onValueChange={setRelatedFilter}>
            <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
              <SelectValue placeholder="Related To" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="investor">Investorer</SelectItem>
              <SelectItem value="customer">Kunder</SelectItem>
              <SelectItem value="none">Ingen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tasks Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[1,2,3].map(i => (
              <div key={i} className="h-64 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-16">
            <CheckSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Ingen oppgaver funnet</h3>
            <p className="text-slate-500 mb-6">Opprett din første oppgave for å komme i gang</p>
            <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Opprett Oppgave
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* To Do Column */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Å Gjøre</h3>
                <span className="text-sm text-slate-500">{tasksByStatus.todo.length}</span>
                </div>
                <TasksList 
                  tasks={tasksByStatus.todo}
                  subtasks={allTasks.filter(t => t.parent_task_id)}
                  timeEntries={timeEntries}
                  onEdit={(task) => {
                    setEditingTask(task);
                    setParentTask(null);
                    setShowForm(true);
                  }}
                  onDelete={(id) => deleteTaskMutation.mutate(id)}
                  onStatusChange={handleStatusChange}
                  onAddSubtask={handleAddSubtask}
                />
                </div>

                {/* In Progress Column */}
                <div>
                <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Pågår</h3>
                <span className="text-sm text-slate-500">{tasksByStatus.in_progress.length}</span>
                </div>
                <TasksList 
                  tasks={tasksByStatus.in_progress}
                  subtasks={allTasks.filter(t => t.parent_task_id)}
                  timeEntries={timeEntries}
                  onEdit={(task) => {
                    setEditingTask(task);
                    setParentTask(null);
                    setShowForm(true);
                  }}
                  onDelete={(id) => deleteTaskMutation.mutate(id)}
                  onStatusChange={handleStatusChange}
                  onAddSubtask={handleAddSubtask}
                />
                </div>

                {/* Completed Column */}
                <div>
                <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Fullført</h3>
                <span className="text-sm text-slate-500">{tasksByStatus.completed.length}</span>
              </div>
              <TasksList 
                tasks={tasksByStatus.completed}
                subtasks={allTasks.filter(t => t.parent_task_id)}
                timeEntries={timeEntries}
                onEdit={(task) => {
                  setEditingTask(task);
                  setParentTask(null);
                  setShowForm(true);
                }}
                onDelete={(id) => deleteTaskMutation.mutate(id)}
                onStatusChange={handleStatusChange}
                onAddSubtask={handleAddSubtask}
              />
            </div>
          </div>
        )}
      </div>

      {/* Task Form */}
      {showForm && (
        <TaskForm
          task={editingTask}
          parentTaskId={parentTask?.id}
          projects={projects}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingTask(null);
            setParentTask(null);
          }}
          isLoading={createTaskMutation.isPending || updateTaskMutation.isPending}
        />
      )}
    </div>
  );
}